import { __assign } from "tslib";
import React, { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import ddt from '@wix/da-ddt';
import { PureBiData } from '@wix/da-bi/pkg/events';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks/useFlag';
import { Url } from '@wix/da-url';
import { SubscriptionPurchaseFTUEContext } from '../../../Tier/ftue/SubscriptionPurchaseFTUE/SubscriptionPurchaseFTUEContext';
import { useIsCurrentUserDeviationAuthor } from '../../../utils/hooks/useIsCurrentUserDeviationAuthor';
import { useRestrictToLoggedIn } from '../../../utils/hooks/useRestrictToLoggedIn';
import ActionBarItemWrapper from '../../_partials/items/ActionBarItemWrapper';
import ActionBarButton from '../../_partials/items/ActionBarButton';
import { formatDollars } from '../../../utils/formatting';
import { getDeviationPremiumDownload } from './permissions';
import PremiumPurchasePopover from './PremiumPurchasePopover';
import s from './PremiumDownloadButton.scss';
var logger = ddt.createLogger('downloadbutton');
var PremiumDownloadButton = function (_a) {
    var _b, _c;
    var deviation = _a.deviation, className = _a.className, onDownload = _a.onDownload;
    // note: mouseEnter and mouseExit aren't used since this button isn't an icon button,
    // doesn't need a tooltip itself, and, bc of that, doesn't need to pause the comment tooltip
    var t = useTranslation().t;
    var _d = useRestrictToLoggedIn(), isLoggedIn = _d.isLoggedIn, restrictToLoggedIn = _d.restrictToLoggedIn;
    var pcp_preview = useFlag('pcp_preview');
    // when the user first purchases a subscription, open the tooltip and highlight the icon
    var isButtonHighlighted = useContext(SubscriptionPurchaseFTUEContext) === 'tooltips';
    // when there's unpurchased premium, show a popover
    var renderPurchasePopover = useCallback(function (_a) {
        var closeTooltip = _a.closeTooltip;
        return (React.createElement(PremiumPurchasePopover, { deviation: deviation, closeTooltip: closeTooltip }));
    }, [deviation]);
    logger.log('PremiumDownloadButton, deviation:', deviation);
    var isOwner = useIsCurrentUserDeviationAuthor(deviation);
    var _e = getDeviationPremiumDownload(deviation, isOwner), download = _e.download, permission = _e.permission;
    logger.log({ isOwner: isOwner, download: download, permission: permission });
    if (!download) {
        logger.log('has no downloads');
        return null;
    }
    var wrapperProps = {
        className: className,
        popoverWrapperClassName: s['wrapper'],
    };
    var buttonProps = {
        className: classnames(s['button'], isButtonHighlighted && s['highlighted']),
        biData: PureBiData({
            evid: 350,
            ispaiddownload: 1,
            deviationid: deviation.deviationId,
        }),
    };
    var buttonLabel = t('actionbar.download');
    var isPurchasable = permission === 'purchasable';
    if (isPurchasable) {
        var price = formatDollars(download.downloadDollarPrice, {
            minimumFractionDigits: 0,
        });
        buttonLabel = price
            ? t('actionbar.downloadFor', { price: price })
            : t('actionbar.download.mustPurchase');
    }
    // send anon to sisu
    if (!isLoggedIn) {
        return (React.createElement(ActionBarItemWrapper, __assign({}, wrapperProps, { tooltip: t('actionbar.download.mustLogIn') }),
            React.createElement(ActionBarButton, __assign({}, buttonProps, { onClick: restrictToLoggedIn(ReferralType.ANON_DOWNLOAD) }), buttonLabel)));
    }
    var shouldUsePurchasePopover = !pcp_preview && isPurchasable;
    var checkoutUrl = ((_b = deviation.pcp) === null || _b === void 0 ? void 0 : _b.subproductId)
        ? Url.checkoutLink((_c = deviation.pcp) === null || _c === void 0 ? void 0 : _c.subproductId)
        : undefined;
    var isLinkingToCheckout = pcp_preview && isPurchasable && !!checkoutUrl;
    return (React.createElement(ActionBarItemWrapper, __assign({}, wrapperProps, { allowMobilePopover: isPurchasable, renderPopover: shouldUsePurchasePopover ? renderPurchasePopover : undefined }),
        React.createElement(ActionBarButton, __assign({}, buttonProps, (isLinkingToCheckout && {
            href: checkoutUrl,
        }), (permission === 'downloadable' && {
            href: download.url,
            target: '_blank',
            download: true,
            onClick: onDownload,
        })), buttonLabel)));
};
PremiumDownloadButton.displayName = 'PremiumDownloadButton';
export default PremiumDownloadButton;
//# sourceMappingURL=PremiumDownloadButton.js.map